import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { Button, FormGroup, Form } from 'react-bootstrap'
import cloneDeep from 'lodash/cloneDeep'
import { Field, Formik, yupToFormErrors, useFormikContext } from 'formik'
import { useServiceContext } from '~/root'
import { useTranslation } from 'react-i18next'
import { RejectionReviewersValidationSchema } from './WFPhaseValidation'
import { transformFromServer } from '~/pages/settings/workflow-templates/DataService'
import Modal from '../Modal'
import { FsiType } from '~/constants/fsi'
import { ErrorMessageStyled } from '../ErrorMessage'
import { WF_STEP_TYPE } from '~/types/workflow'
import SelectUserGroup from '../SelectUserGroup'
import { getFsiRejectionReviewers, updateFsiRejectionReviewersV2 } from '~/services/api/files'

// Component that uses Formik context to handle SelectUserGroup with stable onChange
const SelectUserGroupFormikField = React.memo(({ isDisabled }: { isDisabled?: boolean }) => {
  const { values, setFieldValue } = useFormikContext<any>()

  // Use ref to store the latest setFieldValue without causing re-renders
  const setFieldValueRef = useRef(setFieldValue)

  // Update ref in useLayoutEffect (runs synchronously after render, before browser paint)
  React.useLayoutEffect(() => {
    setFieldValueRef.current = setFieldValue
  })

  // Create stable handler that never changes - uses ref to access latest setFieldValue
  const handleChange = useCallback((option: any) => {
    setFieldValueRef.current('rejectionReviewers.usersGroups', option ? option : [])
  }, [])

  return (
    <SelectUserGroup
      isMulti={true}
      value={values?.rejectionReviewers?.usersGroups}
      onChange={handleChange}
      name="rejectionReviewers.usersGroups"
      isDisabled={isDisabled}
    />
  )
})

SelectUserGroupFormikField.displayName = 'SelectUserGroupFormikField'

interface RejectionReviewersPhaseModalProps {
  fsi?: {
    id?: string | number
    type?: string
    name?: string
  }
  onClose?: () => void
}

interface FormValues {
  workflowTemplate?: {
    id?: string | number
    name?: string
  }
  rejectionReviewers?: {
    type?: string
    usersGroups?: any[]
  }
  readOnly?: boolean
  saveRecursivelyVar?: boolean
}

function RejectionReviewersPhaseModal({ fsi, onClose }: RejectionReviewersPhaseModalProps) {
  const context = useServiceContext()
  const { t: translate } = useTranslation()
  const [isOpen, setIsOpen] = useState(true)
  const [formData, setFormData] = useState<FormValues>({})
  const rejectionReviewersValidationSchema = RejectionReviewersValidationSchema()

  useEffect(() => {
    if (fsi?.id) {
      getFsiRejectionReviewers(Number(fsi.id)).then((data) => {
        setFormData(transformFromServer(data, []))
      })
    }
  }, [fsi])

  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  function doSave(values: FormValues) {
    const cloneVals = cloneDeep(values)
    const saveRecursivelyVar = cloneVals.saveRecursivelyVar
    delete cloneVals.saveRecursivelyVar
    updateFsiRejectionReviewersV2(Number(fsi!.id), cloneVals as any, false, saveRecursivelyVar).then(
      () => {
        doClose()
      },
      (data: any) => {
        context.service.toast.error(
          data?.data?.message ? data.data.message : translate('SOMETHING_WENT_WRONG_MSG_ERROR')
        )
      }
    )
  }

  return (
    <Modal
      show={isOpen}
      handleClose={doClose}
      title={translate('BROWSER_FILE_SETTINGS_REJECTION_REVIEWERS_H_TITLE')}
      size="lg"
    >
      <Formik
        innerRef={(el) => {
          if (el && context.store.formRef !== el) {
            context.store.formRef = el
          }
        }}
        initialValues={{
          ...cloneDeep(formData)
        }}
        enableReinitialize
        validate={(values) => {
          return rejectionReviewersValidationSchema
            .validate(values, {
              abortEarly: false,
              context: values
            })
            .then(
              () => {
                return {}
              },
              (err: any) => {
                return yupToFormErrors(err)
              }
            )
        }}
        onSubmit={(values) => {
          doSave(values)
        }}
      >
        {({
          values,
          handleSubmit,
          setFieldValue
        }: {
          values: FormValues
          handleSubmit: () => void
          setFieldValue: (field: string, value: any) => void
        }) => {
          // Clear usersGroups when switching to USE_INITIATOR
          React.useEffect(() => {
            if (values?.rejectionReviewers?.type === WF_STEP_TYPE.USE_INITIATOR) {
              if (values?.rejectionReviewers?.usersGroups && values.rejectionReviewers.usersGroups.length > 0) {
                setFieldValue('rejectionReviewers.usersGroups', [])
              }
            }
          }, [values?.rejectionReviewers?.type])

          return (
            <>
              <Modal.Body>
                <div className="form-group md-form-group w100p">
                  <label>{fsi?.type === FsiType.FOLDER ? translate('FSI_TYPE_FOLDER') : translate('DOCUMENT')}</label>
                  {fsi?.name ? <p className="whitespaceprewrap fs12">{fsi.name}</p> : null}
                </div>
                {values?.workflowTemplate?.id && (
                  <div className="form-group md-form-group w100p">
                    <label>{translate('WF_TEMPLATE_LABEL')}</label>
                    <p>{values.workflowTemplate?.name || '-'}</p>
                  </div>
                )}
                <FormGroup>
                  <Form.Label>{translate('BROWSER_FILE_SETTINGS_REJECTION_REVIEWERS_TYPE_HEADLINE')}</Form.Label>
                  <Field
                    name="rejectionReviewers.type"
                    value={WF_STEP_TYPE.USE_INITIATOR}
                    checked={values?.rejectionReviewers?.type === WF_STEP_TYPE.USE_INITIATOR}
                    as={Form.Check}
                    type="radio"
                    id={`2c5f11d8-e4d7-4f5b-be00-7864ef173ab2-${WF_STEP_TYPE.USE_INITIATOR}`}
                    disabled={values?.readOnly}
                    label={
                      <div className="px-2 font-weight-normal">
                        {translate('BROWSER_FILE_SETTINGS_REJECTION_REVIEWERS_LABEL_INITIATOR_OF_CR')}
                      </div>
                    }
                  />
                  <Field
                    name="rejectionReviewers.type"
                    value={WF_STEP_TYPE.USE_PREDEFINED}
                    checked={values?.rejectionReviewers?.type === WF_STEP_TYPE.USE_PREDEFINED}
                    as={Form.Check}
                    type="radio"
                    id={`2c5f11d8-e4d7-4o5b-be00-7864ef173ab2-${WF_STEP_TYPE.USE_PREDEFINED}`}
                    disabled={values?.readOnly}
                    label={
                      <div className="px-2 font-weight-normal">
                        {translate('BROWSER_FILE_SETTINGS_REJECTION_REVIEWERS_LABEL_SPECIFIC_USERS')}
                      </div>
                    }
                  />
                </FormGroup>

                {values?.rejectionReviewers?.type === WF_STEP_TYPE.USE_PREDEFINED ? (
                  <>
                    <SelectUserGroupFormikField isDisabled={values?.readOnly} />

                    <ErrorMessageStyled name={`rejectionReviewers.usersGroups`} />
                  </>
                ) : null}
              </Modal.Body>
              <Modal.Footer>
                {!!values?.readOnly && (
                  <Button className="btn btn-primary at_btnSaveApprovers" type="button" onClick={doClose}>
                    {translate('OK')}
                  </Button>
                )}
                {!values?.readOnly && (
                  <>
                    <Button className="btn btn-close" variant="link" type="button" onClick={doClose}>
                      {translate('CANCEL')}
                    </Button>
                    <Button
                      type="submit"
                      className="btn btn-primary at_btnSaveApprovers"
                      onClick={() => {
                        setFieldValue('saveRecursivelyVar', false)
                        handleSubmit()
                      }}
                    >
                      {translate('SAVE_BTN')}
                    </Button>
                  </>
                )}
                {fsi?.type === context.constant.file.type.FOLDER && !values?.readOnly && (
                  <Button
                    type="submit"
                    className="btn btn-primary at_btnSaveAclsRecursively"
                    onClick={() => {
                      setFieldValue('saveRecursivelyVar', true)
                      handleSubmit()
                    }}
                  >
                    {translate('SAVE_FOR_ENCLOSED_ITEMS')}
                  </Button>
                )}
              </Modal.Footer>
            </>
          )
        }}
      </Formik>
    </Modal>
  )
}

export default RejectionReviewersPhaseModal
